import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ChevronDown } from "lucide-react";
import Sidebar from "./Sidebar";
import ActionMenu from "./ActionMenu";

const PageTitleContext = createContext({
  setTitle: () => {},
});

export function usePageTitle(title) {
  const { setTitle } = useContext(PageTitleContext);
  useEffect(() => {
    setTitle(title);
    return () => setTitle("");
  }, [title, setTitle]);
}

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export default function AppShell({
  brand = "Adlywise DSP",
  brandSubtitle,
  roleLabel,
  sections,
  sidebarFooter,
  user,
  userMenuItems = [],
  children,
}) {
  const [title, setTitle] = useState("");
  const value = { setTitle: useCallback((t) => setTitle(t ?? ""), []) };

  return (
    <PageTitleContext.Provider value={value}>
      <div className="flex h-screen w-full overflow-hidden bg-(--canvas) text-(--text-primary)">
        <Sidebar
          brand={brand}
          brandSubtitle={brandSubtitle}
          roleLabel={roleLabel}
          sections={sections}
          footer={sidebarFooter}
        />
        <main className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
          <header className="flex shrink-0 items-center justify-between gap-6 border-b border-(--border-subtle) bg-(--surface-1) px-[30px] py-4">
            <h1 className="truncate text-[27px] font-extrabold tracking-[-0.02em] text-(--text-primary)">
              {title}
            </h1>
            {user && userMenuItems.length > 0 ? (
              <ActionMenu
                ariaLabel="User menu"
                items={userMenuItems}
                trigger={({ toggle, open }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="true"
                    aria-expanded={open}
                    className="flex items-center gap-[10px] border-0 bg-transparent p-0"
                  >
                    <div className="flex h-[38px] w-[38px] items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) text-[13px] font-bold text-(--brand-primary-contrast)">
                      {initialsOf(user.name)}
                    </div>
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-[13px] font-bold text-(--text-primary)">
                        {user.name}
                      </span>
                      {user.role && (
                        <span className="text-[11px] capitalize text-(--text-muted)">
                          {user.role}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className="ml-1 text-(--text-muted)"
                    />
                  </button>
                )}
              />
            ) : null}
          </header>
          <div className="flex-1 overflow-y-auto px-[30px] py-7">
            {children}
          </div>
        </main>
      </div>
    </PageTitleContext.Provider>
  );
}
